import { useEffect, useMemo } from 'react';
import { usePresencePlayers } from '../../../../components/SessionPresence';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectors,
  getIndividualReport,
  fetchAnalysis,
  fetchLeaderboard,
  fetchConcentrations,
  fetchPnLDistributions,
  fetchTurnover,
  fetchMarketRegimes,
  fetchDisplaySeries,
  retrievePlayer,
} from 'state';

import {
  useTrainerAIReportData,
  useTrainerGroupAIReportData,
  useTrainerBiasReportData,
} from '../../AI/aiReportShared';
import { assembleStudioData, NEUTRAL_SESSION } from './buildLive';

/**
 * Live data source for AnalysisStudioLive. Returns the same
 * `{ players, session, groupReport }` shape the page already consumes, built
 * from the real Redux store — all players at once (no single `state.player`).
 *
 * Wired now:
 *  - cohort `last_metrics` + `last_portfolio_return`  → RiskReturnScatter
 *  - `metrics[]` / `player_history[]` + `metrics_limits` / `frequency` → RiskPanel evolution
 *  - `last_metrics`                                    → RiskPanel risk tiles
 *  - session-level all-players `analysis.data`         → RiskPanel coach-read list
 *  - focused player's `report.{trades,return_per_trade,hold_period}` → BehaviorPanel
 *
 * Analysis lives in `sessions.items[session].analysis.data` (all players) and is
 * loaded here via `fetchAnalysis` (the GET path is immune to the trainer/trainee
 * `state.player` guard on the realtime socket handlers). Falls back to mock only
 * when no real session/players exist (defensive — never under a loaded session).
 */
export const useStudio2Data = (focusedId?: number) => {
  const dispatch = useDispatch();
  const realPlayers = usePresencePlayers(useSelector(selectors.playersList));
  const realSession = useSelector(selectors.activeSession);
  const analysisItems = useSelector(selectors.analysisItemsSelector);
  const focusedReport = useSelector(
    selectors.individualReport(focusedId ?? -1),
  );
  const repCcy = useSelector(
    selectors.currencyById(realSession?.reporting_currency ?? -1),
  );
  // Orders + lookup tables for the blotter / open-orders enrichment.
  const allOrders = useSelector(selectors.orders);
  const underlyingAssets = useSelector(selectors.underlyingAssets);
  const currencies = useSelector(selectors.currencies);

  // AI reports — these hooks dispatch the prompt/response fetches and return the
  // store dicts (all players at once, keyed by id).
  const sessionId = realSession?.id ?? 0;
  const { promptsObj, responses } = useTrainerAIReportData(sessionId);
  const { responses: groupResponses } = useTrainerGroupAIReportData(sessionId);
  const { biasReports } = useTrainerBiasReportData(sessionId);

  // Load the session-level analysis (all players) — drives the coach-read list.
  useEffect(() => {
    if (realSession?.id != null) {
      dispatch(fetchAnalysis({ session_id: realSession.id }));
      dispatch(fetchLeaderboard({ session_id: realSession.id }));
      dispatch(fetchConcentrations({ session_id: realSession.id }));
      dispatch(fetchPnLDistributions({ session_id: realSession.id }));
      dispatch(fetchTurnover({ session_id: realSession.id }));
      dispatch(fetchMarketRegimes({ session_id: realSession.id }));
      // Predetermined only: hydrate the display fills the return charts knit in.
      // Fixed/RT sessions never dispatch this (endpoint would return empty lists anyway).
      if (
        realSession.time_advancement_type === 'predetermined_intervals' &&
        !realSession.display_series_fetched
      ) {
        dispatch(fetchDisplaySeries({ session_id: realSession.id }));
      }
    }
  }, [dispatch, realSession?.id]);

  // Prefetch EVERY player's individual report up-front so switching players on
  // the Snapshot / Drivers / Behaviour tabs is instant. The report is the only
  // per-player fetch (concentrations & pnl distributions already arrive for the
  // whole cohort), so loading them all here removes the per-switch flicker.
  // Keyed on the roster signature → runs once per session (not every render).
  const playerIdsKey = realPlayers.map(p => p.id).join(',');
  useEffect(() => {
    if (!realSession?.id) return;
    realPlayers.forEach(p => {
      dispatch(getIndividualReport({ player_id: p.id }));
      // Pulls the player's orders (+ financial_instruments) for the blotter.
      dispatch(retrievePlayer({ player_id: p.id, session_id: realSession.id }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, realSession?.id, playerIdsKey]);

  return useMemo(() => {
    // Real data not loaded yet → an EMPTY payload (never mock) + isReady:false.
    // A real session always has players, so an empty roster means "still
    // loading"; StudioShell renders the loading skeleton on !isReady.
    if (!realSession?.id || !realPlayers?.length) {
      return {
        players: [],
        session: NEUTRAL_SESSION,
        groupReports: [],
        isReady: false,
      };
    }
    return {
      ...assembleStudioData({
        roster: realPlayers,
        session: realSession,
        analysisItems,
        biasReports,
        promptsObj,
        responses,
        groupResponses,
        allOrders,
        underlyingAssets,
        currencies,
        focusedId,
        focusedReport,
        currencySymbol: repCcy?.symbol,
        leaderboard: realSession.leaderboard_scores ?? [],
      }),
      isReady: true,
    };
  }, [
    realPlayers,
    realSession,
    analysisItems,
    focusedReport,
    focusedId,
    repCcy,
    promptsObj,
    responses,
    groupResponses,
    biasReports,
    allOrders,
    underlyingAssets,
    currencies,
  ]);
};
